import {
  FC,
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { useSnackbar } from 'notistack';

import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import {
  useSubscriptionFeatures,
  useParseFiltersRequestSearchPartnerCompanies,
  useSelectionIds,
  useToggle,
  useDataTableRequest,
  useLazyRequest,
  useToggleDialogConfirmation,
} from '~hooks/index';
import { useSignalRContext } from '~contexts/SignalRContext';
import { useClientOnMethod } from '~hooks/useSignalR';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import {
  searchPartnerItemsV2,
  searchPartnerItemsMapV2,
} from '~services/partnerCompany';
import {
  PartnerCompanyItemFilterV2Base,
  PartnerCompanyItem,
} from '~services/partnerCompany/types';
import { ItemMap } from '~services/item/types';
import { searchWarehouseMap } from '~services/warehouse';
import { WarehouseMap } from '~services/warehouse/types';
import { getFeatureAvailableToDate } from '~utils/feature';
import { sortOrderPendingByExpirationDate } from '~utils/order';
import { METHODS_NAME } from '~constants/signalR';

import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../CollectFiltersContext';

import {
  DashboardVariantV2CarrierContentCollectTabContextValue,
  DashboardVariantV2CarrierContentCollectTabDialogDetailsOrderData,
} from './types';
import {
  ITEM_KEY_ID,
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_COLLECT_ITEM_STATES,
} from './utils';

const DashboardVariantV2CarrierContentCollectTabContext =
  createContext<DashboardVariantV2CarrierContentCollectTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    map: {
      enabled: false,
      showMap: {
        open: false,
        toggleOpen: () => {},
      },
      data: {
        orders: {
          data: [],
          loading: true,
          reloadRequest: () => {},
        },
        warehouses: {
          data: [],
          loading: true,
          reloadRequest: () => {},
        },
      },
    },
    isOnlyTodayDelivery: {
      value: false,
      toggleValue: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      confirmValidateOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      cancelOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2CarrierContentCollectTabContext =
  (): DashboardVariantV2CarrierContentCollectTabContextValue =>
    useContext(DashboardVariantV2CarrierContentCollectTabContext);

const DashboardVariantV2CarrierContentCollectTabProvider: FC = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hubConnection } = useSignalRContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const { filters, forceRequest } =
    useDashboardVariantV2CarrierCollectFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchPartnerCompanies({
    filters: filters.filters,
    defaultCollectItemState: DEFAULT_COLLECT_ITEM_STATES,
  });

  const [isOnlyTodayDelivery, toggleIsOnlyTodayDelivery] = useToggle(false);

  const selection = useSelectionIds<string>([]);

  const [showMap, toogleShowMap] = useToggle(
    hasAccessToFeature(
      PlanFeaturesTypes.MgmItemMap,
      getFeatureAvailableToDate(PlanFeaturesTypes.MgmItemMap),
    ),
  );

  const showMapRef = useRef(showMap);
  showMapRef.current = showMap;

  const table = useDataTableRequest<
    PaginationRequestParams<PartnerCompanyItemFilterV2Base>,
    DataPaginateResponse<PartnerCompanyItem[]>,
    ErrorResponse
  >({
    request: searchPartnerItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
    transformResponse: (res) => {
      const response = res.data as DataPaginateResponse<PartnerCompanyItem[]>;

      if (response.data.results) {
        sortOrderPendingByExpirationDate(response.data.results);

        if (
          response.data.pagination.offset === DEFAULT_PAGINATION.page &&
          response.data.results.length > 0 &&
          !showMapRef.current
        ) {
          const selectedIds = response.data.results.map(
            (item) => item[ITEM_KEY_ID],
          );

          selection.utils.reset();
          selection.utils.addMultiple(selectedIds);
        }
      }

      return response;
    },
  });

  const [mapOrders, setMapOrders] = useState<ItemMap[]>([]);
  const [, loadingMapOrdes, , executeGetMapOrders] = useLazyRequest({
    initialState: [],
    request: searchPartnerItemsMapV2,
    withPostSuccess: (response) => {
      const orders = response.data?.data.results as ItemMap[];

      setMapOrders(orders);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const reloadMapOrders = useCallback(
    ({
      withInitialFilters,
      currentFilters,
    }: {
      withInitialFilters: boolean;
      currentFilters?: Partial<ItemsSearchFilters>;
    }) => {
      if (showMap) {
        setMapOrders([]);

        const filtersRequest = withInitialFilters
          ? DEFAULT_FILTERS_PAYLOAD
          : parseFiltersRequest(currentFilters);

        executeGetMapOrders(filtersRequest);
      }
    },
    [executeGetMapOrders, parseFiltersRequest, showMap],
  );

  const [mapWarehouses, setMapWarehouses] = useState<WarehouseMap[]>([]);
  const [, loadingMapWarehouses, , executeGetMapWarehouses] = useLazyRequest({
    initialState: [],
    request: searchWarehouseMap,
    withPostSuccess: (response) => {
      const warehouses = response.data?.data.results as WarehouseMap[];

      setMapWarehouses(warehouses);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const reloadMapWarehouses = useCallback(() => {
    if (showMap) {
      setMapWarehouses([]);
      executeGetMapWarehouses(null);
    }
  }, [executeGetMapWarehouses, showMap]);

  useEffect(() => {
    reloadMapOrders({ withInitialFilters: false });
    reloadMapWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      reloadMapOrders({ withInitialFilters: false, currentFilters });

      reloadMapWarehouses();

      table.handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [
      forceRequest,
      parseFiltersRequest,
      reloadMapOrders,
      reloadMapWarehouses,
      selection.utils,
      table,
    ],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    if (table.queryParams.pagination.page === DEFAULT_PAGINATION.page) {
      table.reloadRequest();
    } else {
      table.handleChangeTable('filter', {
        pagination: table.queryParams.pagination,
        filters: parseFiltersRequest(),
      });
    }

    reloadMapOrders({ withInitialFilters: false });
  }, [selection.utils, table, reloadMapOrders, parseFiltersRequest]);

  useClientOnMethod(
    hubConnection,
    METHODS_NAME.REFRESH_CARRIER_RECEIVE_ORDERS,
    () => handleForceRequests(),
  );

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentCollectTabDialogDetailsOrderData>
    >(null);

  const [
    openConfirmValidateOrder,
    selectedValidateOrder,
    handleOpenConfirmValidateOrder,
    handleCloseConfirmValidateOrder,
  ] = useToggleDialogConfirmation<Nullable<PartnerCompanyItem>>(null);

  const [
    openDialogCancelOrder,
    selectedCancelOrder,
    handleOpenDialogCancelOrder,
    handleCloseDialogCancelOrder,
  ] = useToggleDialogConfirmation<Nullable<PartnerCompanyItem>>(null);

  return (
    <DashboardVariantV2CarrierContentCollectTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        table,
        map: {
          enabled: true,
          showMap: {
            open: showMap,
            toggleOpen: toogleShowMap,
          },
          data: {
            orders: {
              data: mapOrders,
              loading: loadingMapOrdes,
              reloadRequest: reloadMapOrders,
            },
            warehouses: {
              data: mapWarehouses,
              loading: loadingMapWarehouses,
              reloadRequest: reloadMapWarehouses,
            },
          },
        },
        isOnlyTodayDelivery: {
          value: isOnlyTodayDelivery,
          toggleValue: toggleIsOnlyTodayDelivery,
        },
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
          confirmValidateOrder: {
            open: openConfirmValidateOrder,
            data: selectedValidateOrder,
            handleOpen: handleOpenConfirmValidateOrder,
            handleClose: handleCloseConfirmValidateOrder,
          },
          cancelOrder: {
            open: openDialogCancelOrder,
            data: selectedCancelOrder,
            handleOpen: handleOpenDialogCancelOrder,
            handleClose: handleCloseDialogCancelOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentCollectTabContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentCollectTabProvider;
